import { useEffect, useState } from "react";
import { http } from "../api";
import { Settings2, Check, Globe, Palette } from "lucide-react";
import { PageHeader, FormRow } from "../shared";

export default function TenantSettings({ user }) {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    http.get("/tenants/current").then((r) => {
      setTenant(r.data.tenant);
      if (r.data.tenant?.branding) setForm(r.data.tenant.branding);
    });
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const r = await http.patch("/tenants/current/branding", form);
      setTenant(r.data);
      setMsg("Branding updated. Reload the workspace to see the new theme applied.");
    } catch (e) {
      setMsg("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!tenant) {
    return (
      <>
        <PageHeader eyebrow="WORKSPACE" title="Tenant settings" />
        <section className="panel" style={{ padding: 30 }}>
          <p className="hint">This account is not part of a self-service tenant. It was created directly in the reference workspace. To try white-label + tenant features, sign up a new workspace from the login screen.</p>
        </section>
      </>
    );
  }

  const daysLeft = Math.max(0, Math.round((new Date(tenant.plan_ends_at) - new Date()) / 86400000));

  return (
    <>
      <PageHeader eyebrow="WORKSPACE" title="Tenant & white-label"
        subtitle={`Slug: ${tenant.slug} · Plan: ${tenant.plan} · ${daysLeft} days left`} />

      {msg && <div className="notice-strip" data-testid="tenant-msg" style={{ background: "#e5f2e3", color: "#3a7647", borderColor: "#c1dbb9" }}><Check size={16} />{msg}</div>}

      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-head"><h2><Palette size={16} /> Branding</h2><p>What your team & voters see</p></div>
          <div style={{ padding: 22 }}>
            <FormRow label="Display name (workspace)"><input data-testid="branding-display-name" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></FormRow>
            <FormRow label="Logo URL (SVG/PNG)"><input data-testid="branding-logo" value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" /></FormRow>
            <div className="form-grid">
              <FormRow label="Primary colour (hex)"><input data-testid="branding-primary" value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="#0F766E" /></FormRow>
              <FormRow label="Accent colour (hex)"><input data-testid="branding-accent" value={form.accent_color || ""} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} placeholder="#F0BD61" /></FormRow>
            </div>
            <FormRow label="Login page headline"><input data-testid="branding-headline" value={form.login_headline || ""} onChange={(e) => setForm({ ...form, login_headline: e.target.value })} /></FormRow>
            <div className="form-grid">
              <FormRow label="Tagline (EN)"><input data-testid="branding-tagline-en" value={form.tagline_en || ""} onChange={(e) => setForm({ ...form, tagline_en: e.target.value })} placeholder="Nabadwip · Ward 7 command center" /></FormRow>
              <FormRow label="Tagline (BN)"><input data-testid="branding-tagline-bn" value={form.tagline_bn || ""} onChange={(e) => setForm({ ...form, tagline_bn: e.target.value })} placeholder="নবদ্বীপ · ওয়ার্ড ৭" style={{ fontFamily: "'Noto Sans Bengali'" }} /></FormRow>
            </div>
            <FormRow label="Landing hero image URL (optional)"><input data-testid="branding-hero" value={form.landing_hero_image || ""} onChange={(e) => setForm({ ...form, landing_hero_image: e.target.value })} placeholder="https://…" /></FormRow>
            <div className="ss-preview" data-testid="tenant-branding-preview" style={{ marginTop: 12, "--pv-primary": form.primary_color || "#0f766e", "--pv-accent": form.accent_color || "#ea823f" }}>
              <div className="ss-preview-title">Workspace preview (overrides global)</div>
              <div className="ss-preview-topbar">
                <div className="ss-preview-mark">
                  {form.logo_url ? <img src={form.logo_url} alt="" /> : <span>{(form.display_name || tenant.org_name || "W").slice(0, 2).toUpperCase()}</span>}
                </div>
                <div>
                  <b>{form.display_name || tenant.org_name || "Workspace"}</b>
                  <small>{form.tagline_en || form.tagline || "Add a tagline"}</small>
                </div>
              </div>
              <div className="ss-preview-btn ss-preview-btn-primary">Primary</div>
              <div className="ss-preview-btn ss-preview-btn-accent">Accent</div>
              <small className="hint" style={{ display: "block", marginTop: 6, textAlign: "center" }}>Landing mode stays global (Super Admin controlled).</small>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head"><h2><Globe size={16} /> Domain</h2><p>Where your workspace lives</p></div>
          <div style={{ padding: 22 }}>
            <FormRow label="Custom domain (optional)"><input data-testid="branding-domain" value={form.custom_domain || ""} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })} placeholder="crm.mypartyname.in" /></FormRow>
            <div className="notice-strip" style={{ marginTop: 12 }}>
              <span>To activate a custom domain, add these DNS records at your registrar:</span>
            </div>
            <pre style={{ background: "#f0f5f0", padding: 12, fontSize: 11, fontFamily: "'DM Mono'", margin: "10px 0", overflowX: "auto" }}>
{`CNAME  ${form.custom_domain || "crm.mypartyname.in"}   →   ${tenant.slug}.nabadwip.crm
TXT    _crm-verify.${form.custom_domain || "crm.mypartyname.in"}   →   ${tenant.tenant_id}`}
            </pre>
            <small className="hint">Once DNS resolves (typically 5-30 minutes), your platform admin issues an SSL cert and enables the route in the ingress. For self-hosted deployments, mount a wildcard cert or use cert-manager + Let&apos;s Encrypt with the tenant ID for DNS-01 challenge.</small>
          </div>
        </section>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15 }}>
        <button className="primary" data-testid="branding-save" onClick={save} disabled={saving}><Settings2 size={16} /> {saving ? "Saving…" : "Save branding"}</button>
      </div>
    </>
  );
}
